import { Injectable, computed, inject, signal } from '@angular/core';
import { FairDirectoryPort, type PublicFair } from '@soft2code/nexpo-lib/api';
import { ThemeService } from '@soft2code/nexpo-lib/theme';
import { firstValueFrom } from 'rxjs';

/**
 * A feira em que o visitante está.
 *
 * Carregada pelo endereço (`/feira-exemplo`) antes de qualquer tela da feira,
 * pelo `fairGuard`. Ao carregar, aplica a cor da feira no acento do app — a
 * navegação já mostra de que feira é.
 *
 * Sem `FairDirectoryPort` (produção, até a leitura pública existir), nenhuma
 * feira é encontrada: o app diz isso em vez de inventar uma.
 */
@Injectable({ providedIn: 'root' })
export class FairContext {
	readonly #directory = inject(FairDirectoryPort, { optional: true });
	readonly #theme = inject(ThemeService);

	readonly #fair = signal<PublicFair | null>(null);

	readonly fair = this.#fair.asReadonly();
	readonly eventId = computed(() => this.#fair()?.eventId ?? null);
	readonly slug = computed(() => this.#fair()?.slug ?? null);

	/** Carrega a feira do endereço. Devolve `null` quando ela não existe ou não está publicada. */
	async load(slug: string): Promise<PublicFair | null> {
		if (this.#fair()?.slug === slug) return this.#fair();
		const fair = this.#directory ? await firstValueFrom(this.#directory.bySlug(slug)) : null;

		this.#fair.set(fair);
		this.#theme.applyFairColor(fair?.color ?? null);
		return fair;
	}

	/** Sai da feira: sem feira, sem a cor dela. */
	clear(): void {
		if (!this.#fair()) return;
		this.#fair.set(null);
		this.#theme.applyFairColor(null);
	}
}
